import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../../core/apiClient';
import { parserService, getParserErrorMessage } from './parserService';
import type {
  ParserPhase,
  ParsingMethod,
  ClassificationResult,
  IngestionResult,
  ProcessingStats,
  ParserLogEntry,
  Step,
} from './types';

const steps: Step[] = [
  { id: 'upload', label: 'Upload', description: 'Select document' },
  { id: 'classify', label: 'Analyze', description: 'AI classification' },
  { id: 'process', label: 'Process', description: 'Extract & structure' },
  { id: 'complete', label: 'Complete', description: 'Template ready' },
];

export const useParserState = () => {
  const navigate = useNavigate();

  // Phase management
  const [currentPhase, setCurrentPhase] = useState<ParserPhase>('upload');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // File management
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsingMethod, setParsingMethod] = useState<ParsingMethod>(null);

  // AI description
  const [aiDescription, setAiDescription] = useState('');
  const [aiDraft, setAiDraft] = useState<any>(null);

  // Results
  const [classificationResult, setClassificationResult] = useState<ClassificationResult | null>(null);
  const [ingestionResult, setIngestionResult] = useState<IngestionResult | null>(null);

  // Error handling
  const [error, setError] = useState<string | null>(null);

  // Progress tracking
  const [processingProgress, setProcessingProgress] = useState(0);
  const [parserLog, setParserLog] = useState<ParserLogEntry[]>([]);
  const parserLogRef = useRef<HTMLDivElement>(null);

  // Statistics
  const [stats, setStats] = useState<ProcessingStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Warn the user before they reload/close the tab mid-parse. Reloading
  // during an active parse cost the platform several duplicate Anthropic
  // runs before the server-side lock was in place — this confirm dialog
  // gives the user one last chance to wait it out.
  useEffect(() => {
    if (currentPhase !== 'processing') return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'A parse is in progress. If you reload now the template may land but the UI will lose track. Stay on the page until it finishes.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [currentPhase]);

  // Fetch statistics
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const response = await apiClient.getInstance().get('/parser/stats');
        if (response.data?.data) {
          setStats(response.data.data);
        }
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setStats({
            documentsProcessed: 0,
            successRate: 0,
            avgProcessingTime: 0,
            frameworksCreated: 0,
            templatesCreated: 0,
            completed: 0,
            failed: 0,
            processing: 0,
            totalNodes: 0,
            totalFields: 0,
          });
        } else {
          console.error('Failed to fetch parser stats:', err);
          setStats({
            documentsProcessed: 0,
            successRate: 0,
            avgProcessingTime: 0,
            frameworksCreated: 0,
            templatesCreated: 0,
            completed: 0,
            failed: 0,
            processing: 0,
            totalNodes: 0,
            totalFields: 0,
          });
          setError(getParserErrorMessage(err));
        }
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Poll for real-time parser progress
  useEffect(() => {
    if (currentPhase !== 'processing') return;

    let lastEventIndex = 0;
    const pollInterval = setInterval(async () => {
      try {
        const data = await parserService.getProgress(lastEventIndex);
        if (data) {
          const newEvents = data.events || [];
          if (newEvents.length > 0) {
            setParserLog((prev) => {
              const merged = [...prev];
              for (const evt of newEvents) {
                merged.push({
                  step: evt.step,
                  message: evt.message,
                  detail: evt.detail,
                  progress: evt.progress || 0,
                  timestamp: new Date(evt.timestamp).toLocaleTimeString(),
                  data: evt.data,
                });
              }
              return merged;
            });
            lastEventIndex = data.totalEvents;
          }
          if (typeof data.progress === 'number' && data.progress > 0) {
            setProcessingProgress(data.progress);
          }
          // Auto-scroll
          setTimeout(() => {
            if (parserLogRef.current) {
              parserLogRef.current.scrollTop = parserLogRef.current.scrollHeight;
            }
          }, 100);
          // Stop polling when complete
          if (data.isComplete) {
            clearInterval(pollInterval);
          }
        }
      } catch {}
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [currentPhase]);

  // Handle AI framework generation
  useEffect(() => {
    if (currentPhase === 'ai_describe' && aiDescription.trim()) {
      const generateAIFramework = async () => {
        try {
          setCurrentPhase('ai_processing');
          const response = await parserService.generateAIFramework(
            aiDescription,
            aiDescription.split(' ').slice(0, 2).join(' ')
          );

          if (response) {
            setAiDraft(response);
            setCurrentPhase('ai_preview');
          } else {
            setError('Failed to generate framework');
            setCurrentPhase('upload');
          }
        } catch (err: any) {
          setError(getParserErrorMessage(err));
          setCurrentPhase('upload');
        }
      };

      generateAIFramework();
    }
  }, [currentPhase, aiDescription]);

  // Actions
  const handleFileSelect = useCallback((file: File) => {
    const allowedMime = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'text/plain',
    ];
    const allowedExt = ['.pdf', '.docx', '.doc', '.xlsx', '.xls', '.csv', '.png', '.jpg', '.jpeg', '.txt'];
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    const mimeOk =
      allowedMime.some((type) => file.type === type || file.type.startsWith(type.split('/')[0] + '/')) ||
      allowedExt.includes(ext);

    if (!mimeOk) {
      setError('Unsupported file. Accepted: PDF, DOCX, XLSX, CSV, PNG, JPG, TXT');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setError('File size must be less than 20MB');
      return;
    }

    setSelectedFile(file);
    setError(null);
  }, []);

  const classifyDocument = useCallback(async () => {
    if (!selectedFile) return;

    setCurrentPhase('classifying');
    setCurrentStepIndex(1);
    setError(null);

    try {
      const result = await parserService.classifyDocument(selectedFile);
      setClassificationResult(result);
      setCurrentPhase('classification_result');
    } catch (err: any) {
      setError(getParserErrorMessage(err));
      setCurrentPhase('error');
    }
  }, [selectedFile]);

  const proceedWithProcessing = useCallback(async () => {
    if (!selectedFile) return;

    setCurrentPhase('processing');
    setCurrentStepIndex(2);
    setError(null);
    setProcessingProgress(0);
    setParserLog([]);

    // Add initial log entry
    setParserLog([
      {
        step: 'upload',
        message: `Uploading "${selectedFile.name}" (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)...`,
        progress: 0,
        timestamp: new Date().toLocaleTimeString(),
      },
    ]);

    try {
      const result = await parserService.ingestDocument(selectedFile);
      setIngestionResult(result);
      setCurrentPhase('completed');
      setCurrentStepIndex(3);
    } catch (err: any) {
      setError(getParserErrorMessage(err));
      setCurrentPhase('error');
    }
  }, [selectedFile]);

  const resetParser = useCallback(() => {
    setCurrentPhase('upload');
    setCurrentStepIndex(0);
    setSelectedFile(null);
    setClassificationResult(null);
    setIngestionResult(null);
    setError(null);
    setProcessingProgress(0);
    setParsingMethod(null);
    setAiDescription('');
    setAiDraft(null);
    setParserLog([]);
  }, []);

  const viewTemplate = useCallback(() => {
    if (ingestionResult?.template?.id) {
      navigate(`/compliance/definition/builder?templateId=${ingestionResult.template.id}`);
    } else {
      navigate('/compliance/definition/templates');
    }
  }, [navigate, ingestionResult]);

  return {
    // Phase state
    currentPhase,
    currentStepIndex,
    setCurrentPhase,
    steps,

    // File state
    selectedFile,
    setSelectedFile,
    parsingMethod,
    setParsingMethod,
    handleFileSelect,

    // AI state
    aiDescription,
    setAiDescription,
    aiDraft,
    setAiDraft,

    // Results
    classificationResult,
    setClassificationResult,
    ingestionResult,
    setIngestionResult,

    // Error
    error,
    setError,

    // Progress
    processingProgress,
    parserLog,
    parserLogRef,

    // Stats
    stats,
    statsLoading,

    // Actions
    classifyDocument,
    proceedWithProcessing,
    resetParser,
    viewTemplate,
  };
};
